import React, { useMemo } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem } from '@mui/material';
import { connect } from 'react-redux';
import CurrencyLabel from '../../../components/CurrencyComponent/CurrencyLabel';
import { orderBy } from 'lodash';

interface Props {
    open: boolean;
    onClose: () => void;
    lot: any;
    images: any;
    settings: any;
}

const BulkUploaderPreviewLotsModal: React.FC<Props> = props => {
    const { open, onClose, lot, images, settings } = props;

    const [activeTab, setActiveTab] = React.useState('0');

    const toggle = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Each value as a small label / value tile (same values and fallbacks as before).
    const renderAmount = (title, value, prefix: string | undefined = undefined, allowFallback = false) =>
        <div className="pb-kv">
            <div className="pb-kv-key">{title}</div>
            <div className="pb-kv-value">
                {value ? <CurrencyLabel value={value} prefixString={prefix} hideCurrencySymbol={prefix ? true : undefined} /> : allowFallback ? <span className="pb-kv-muted">(Use auction default)</span> : <span className="pb-kv-muted">-</span>}
            </div>
        </div>

    const renderItem = (title, value, allowFallback = false, wide = false) =>
        <div className={"pb-kv" + (wide ? " pb-kv--wide" : "")}>
            <div className="pb-kv-key">{title}</div>
            <div className="pb-kv-value">
                {value ? <div>{value}</div> : allowFallback ? <span className="pb-kv-muted">(Use auction default)</span> : <span className="pb-kv-muted">-</span>}
            </div>
        </div>

    const orderedImages = useMemo(() => {
        const bannerImages = images?.filter(x => (x.order % 1) === 0);
        const otherImages = images?.filter(x => (x.order % 1) > 0);

        return {
            bannerImage: bannerImages?.[0],
            otherImages: orderBy(otherImages ?? [], "order")
        }
    }, [images])

    const imageSource = (image) => image?.fileData ?? image?.imageUrl;
    const hasErrors = lot?.validationErrors?.length > 0 || lot?.bannerImageCount !== 1 || lot?.otherImageCount === 0 || lot?.otherImageCount > 50;

    return !open ? null :
        <Dialog
            fullWidth
            maxWidth={"md"}
            open={open}
            onClose={onClose}
            aria-labelledby="max-width-dialog-title">
            <DialogTitle id="max-width-dialog-title">{lot.name}</DialogTitle>
            <DialogContent className="pb-preview-tabs">
                <List className="nav-tabs nav-tabs-primary d-flex">
                    <ListItem button disableRipple selected={activeTab === '0'} onClick={() => { toggle('0'); }}>Data</ListItem>
                    <ListItem button disableRipple selected={activeTab === '1'} onClick={() => { toggle('1'); }}>Images</ListItem>
                    <ListItem button disableRipple selected={activeTab === '2'} onClick={() => { toggle('2'); }}>
                        Validation Errors
                        {hasErrors && <span className="pb-tab-dot" aria-label="Has validation errors" />}
                    </ListItem>
                </List>
            </DialogContent>
            <DialogContent style={{ height: '60vh', overflowY: 'auto' }}>
                {activeTab === '0' && <div className={"tab-item-wrapper active pb-kv-grid pb-preview-data"} key={0}>
                    {renderItem("Description", lot.description, false, true)}
                    {renderItem(settings.LotName + " Type", lot.lotTypeName)}
                    {renderItem("STC Status", lot.stcStatus === 1 ? "Yes" : lot.stcStatus === 0 ? "No" : undefined, true)}
                    {renderItem("Display Address on Client Portal", lot.locationVisible === true ? "Yes" : lot.locationVisible ? "No" : undefined)}
                    {renderItem("Location", lot.locationSearch)}
                    {renderItem("Latitude", lot.locationLatitude)}
                    {renderItem("Longitude", lot.locationLongitude)}
                    {renderAmount("Buyers Commission %", lot.commission, "%", true)}
                    {renderAmount("Opening Bid", lot.startingPrice)}
                    {renderAmount("Increment", lot.defaultMinimumIncrement, undefined, true)}
                    {renderAmount("Reserve Price", lot.reservePrice)}
                    {renderItem("Show when the Reserve is Met", lot.showReserve === true ? "Yes" : lot.showReserve ? "No" : undefined)}
                    {renderAmount("Guide Price", lot.guidePrice)}
                    {renderItem("SKU Number", lot.skuNumber)}
                    {renderItem(settings.LotName + " Video URL", lot.videoUrl)}
                    {renderItem("Late Bid Extension (seconds)", lot.defaultBidExtension, true)}
                    {renderItem("Late Bid Extension Window (seconds)", lot.defaultBidExtensionWindow, true)}
                </div>}

                {activeTab === '1' && <div className={"tab-item-wrapper active pb-preview-images"} key={1}>
                    {/* The banner as the client portal shows it (16:9), then the other images in their order. */}
                    <div className="pb-preview-block">
                        <div className="pb-preview-heading">Banner image</div>
                        {orderedImages?.bannerImage
                            ? <div className="pb-preview-banner"><img src={imageSource(orderedImages.bannerImage)} alt="" /></div>
                            : <div className="pb-preview-empty">No image uploaded</div>}
                    </div>
                    <div className="pb-preview-block">
                        <div className="pb-preview-heading">
                            {settings.LotName} images
                            {orderedImages?.otherImages?.length > 0 && <span className="pb-uploader-counter">{orderedImages.otherImages.length} / 50 photos</span>}
                        </div>
                        {orderedImages?.otherImages?.length > 0
                            ? <div className="pb-photo-grid pb-preview-grid">
                                {orderedImages.otherImages.map((image, index) => (
                                    <div className="pb-photo" key={index}>
                                        <img src={imageSource(image)} alt="" />
                                        <span className="pb-photo-order">{index + 1}</span>
                                    </div>
                                ))}
                            </div>
                            : <div className="pb-preview-empty">No image uploaded</div>}
                    </div>
                </div>}

                {activeTab === '2' && <div className={"tab-item-wrapper active"} key={2}>
                    {hasErrors
                        ? <ul className="pb-preview-errors">
                            {lot.validationErrors?.length > 0 && lot.validationErrors.map((error, index) => <li key={index}>{error}</li>)}
                            {lot.bannerImageCount !== 1 && <li>Please upload a minimum of 1 image for the banner image</li>}
                            {(lot.otherImageCount === 0 || lot.otherImageCount > 50) && <li>Please upload a minimum of 1 and a maximum of 50 images for the {settings.LotName} images</li>}
                        </ul>
                        : <div className="pb-preview-ok">No validation errors</div>}
                </div>}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                    className="btn-primary text-white text-capitalize">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
}

const mapStateToProps = (state: any) => ({
    settings: state.settings.settings,
})


const mapDispatchToProps = (dispatch: any) => {
    return {
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(BulkUploaderPreviewLotsModal);
